import React, { useRef, ReactElement } from 'react';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import type { Instance } from 'tippy.js';
import './dropdown-menu-tippy.css'; // デフォルトのスタイルを読み込み

// DropdownMenu用の拡張MenuItem型
export interface DropdownMenuItem {
  label: string;
  onClick: () => void;
  color?: string;
  icon?: string;
  disabled?: boolean;
}

export interface DropdownMenuProps {
  items: DropdownMenuItem[];
  placement?: 'bottom-end' | 'bottom-start' | 'top-end' | 'top-start' | 'bottom' | 'top';
  /** トリガー（3点リーダー）の大きさ。small はサイドバー等の行内配置用 */
  size?: 'small' | 'medium';
  /**
   * トリガー要素の差し替え（省略時は 3 点リーダーのアイコン）。
   * ref を受け取れる単一の DOM 要素（button 等）を渡すこと。
   */
  trigger?: ReactElement;
}

const MenuItem = styled.div<{ $disabled?: boolean }>`
  padding: 8px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  color: ${({ $disabled }) => ($disabled ? "#a7a8a9" : "#000")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? "transparent" : "#fcfcfc")};
  }
`;

const MoreHoriz = styled.div.attrs({
  className: 'material-symbols-rounded'
})<{ $size: 'small' | 'medium' }>`
  font-size: ${({ $size }) => ($size === 'small' ? '20px' : '24px')};
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none; /* Safari用 */
  -moz-user-select: none; /* Firefox用 */
  -ms-user-select: none; /* IE/Edge用 */
  transition: color 0.2s;

  /* フォーム内アイコン（.c-icon-* ）と同じ、light-gray → blue のホバー。
     置かれる先に .banner-colorN div のような「子孫の div を一律で塗る」指定が
     あると単一クラス（0,1,0）では負けるため、&&（0,2,0）で詳細度を上げて
     自分の色を必ず通す。 */
  && {
    color: var(--soremo-light-gray);
  }

  &&:hover {
    color: var(--soremo-blue);
  }
`;

const DropdownMenu: React.FC<DropdownMenuProps> = ({ items, placement = 'bottom-end', size = 'medium', trigger }) => {
  const tippyRef = useRef<Instance | null>(null);

  const handleItemClick = (onClick: () => void) => {
    tippyRef.current?.hide();
    onClick();
  };

  const renderMenu = () => {
    return (
      <>
        {items.map((item, index) => (
          <MenuItem
            key={index}
            $disabled={item.disabled}
            onClick={() => {
              if (!item.disabled) handleItemClick(item.onClick);
            }}
          >
            <p className="bodytext-13" style={{ color: item.color }}>{item.label}</p>
            {item.icon && <div className="material-symbols-rounded u-fontsize-24" style={{ color: item.color }}>{item.icon}</div>}
          </MenuItem>
        ))}
      </>
    );
  };

  return (
    <Tippy
      theme="dropdown-menu"
      animation="scale"
      duration={[100, 50]}
      content={renderMenu()}
      trigger="click"
      interactive={true}
      placement={placement}
      arrow={false}
      offset={[0, 4]}
      appendTo={() => document.body}
      onCreate={(instance) => { tippyRef.current = instance; }}
    >
      {/** ▼ トリガーになるボタン */}
      {trigger ?? <MoreHoriz $size={size}>more_horiz</MoreHoriz>}
    </Tippy>
  );
};

export default DropdownMenu;

